import {
  isPickupLoanSelectable,
  type ObLockCacheRow,
  type PickupLoan
} from './types';
import {
  formatPickupInvestorDisplayName,
  formatPickupInvestorRoute
} from './pickupInvestorDisplay';
import { pickupPointsToDollars } from './pickupPricingMath';
import { pickupVerificationSeverity } from './pickupVerificationPresentation';

export type PickupAnalysisSegment = 'active' | 'expired' | 'funded' | 'needs-review' | 'history';
export type PickupGainFilter = 'all' | 'trigger' | 'positive' | 'none';
export type PickupVerificationFilter = 'all' | 'none' | 'minor' | 'major' | 'not_checked';

export const PICKUP_ANALYSIS_HEADER_SLOT_ID = 'pickup-analysis-workspace-header-actions';
export const PICKUP_MANUAL_REPRICE_REASON_WINDOW_MS = 60 * 60 * 1000;

export interface PickupRefreshRequest {
  action: 'refreshCache';
  forceReverifyAll?: true;
}

const PICKUP_DETAIL_STATUS_KEYS = new Set([
  'locked',
  'lockpending',
  'lockrequested',
  'lockextended',
  'expired'
]);

export function buildPickupRefreshRequest(forceReverifyAll: boolean): PickupRefreshRequest {
  return forceReverifyAll
    ? { action: 'refreshCache', forceReverifyAll: true }
    : { action: 'refreshCache' };
}

export function countPickupForceReverifyTargets(rows: ObLockCacheRow[]): number {
  return rows.filter(isPickupForceReverifyTarget).length;
}

export function isPickupForceReverifyTarget(row: ObLockCacheRow): boolean {
  if (row.cache_frozen_at) {
    return false;
  }
  const status = (row.ob_status ?? '').trim().toLowerCase().replace(/[\s_-]+/g, '');
  return PICKUP_DETAIL_STATUS_KEYS.has(status);
}

export function pickupLoanRequiresManualRepriceReason(
  loan: Pick<PickupLoan, 'pickup_last_priced_at'>,
  nowMs = Date.now()
): boolean {
  const lastPricedAtMs = loan.pickup_last_priced_at
    ? Date.parse(loan.pickup_last_priced_at)
    : Number.NaN;
  return Number.isFinite(lastPricedAtMs)
    && lastPricedAtMs >= nowMs - PICKUP_MANUAL_REPRICE_REASON_WINDOW_MS;
}

export interface PickupAnalysisFilters {
  gain: PickupGainFilter;
  verification: PickupVerificationFilter;
  investor: string;
}

export const PICKUP_ANALYSIS_DEFAULT_FILTERS: PickupAnalysisFilters = {
  gain: 'all',
  verification: 'all',
  investor: 'all'
};

export interface PickupAnalysisFilterCounts {
  gain: Record<Exclude<PickupGainFilter, 'all'>, number>;
  verification: Record<Exclude<PickupVerificationFilter, 'all'>, number>;
  investors: Array<{ investor: string; count: number }>;
}

export interface PickupLoanBestPricing {
  currentInvestor: string;
  bestInvestor: string;
  investorRoute: string;
  bestPrice: number | null;
  pickupPoints: number | null;
  pickupDollars: number | null;
}

export interface PickupAnalysisGridSummary {
  rowCount: number;
  withPickupCount: number;
  potentialDollars: number;
  volumeDollars: number;
}

function finitePrice(value: number | null): number | null {
  return value !== null && Number.isFinite(value) ? value : null;
}

function pickupLoanAmount(loan: Pick<PickupLoan, 'base_loan_amount' | 'trans_details_loan_amt'>): number | null {
  const amount = loan.base_loan_amount || loan.trans_details_loan_amt;
  return amount !== null && Number.isFinite(amount) ? amount : null;
}

export function pickupLoanBestPricing(loan: Pick<
  PickupLoan,
  | 'locked_price'
  | 'current_investor_price'
  | 'best_price'
  | 'best_investor'
  | 'ob_investor_name'
  | 'investor_name'
  | 'base_loan_amount'
  | 'trans_details_loan_amt'
>): PickupLoanBestPricing {
  const currentInvestor = pickupLoanInvestor(loan);
  const currentPrice = finitePrice(loan.current_investor_price);
  const alternativePrice = finitePrice(loan.best_price);
  const alternativeInvestor = formatPickupInvestorDisplayName(loan.best_investor);
  const alternativeWins = alternativePrice !== null
    && (currentPrice === null || alternativePrice > currentPrice);
  const bestPrice = alternativeWins ? alternativePrice : currentPrice;
  const bestInvestor = alternativeWins && alternativeInvestor
    ? alternativeInvestor
    : currentInvestor;
  const investorRoute = formatPickupInvestorRoute(currentInvestor, bestInvestor);
  const lockPrice = finitePrice(loan.locked_price);
  const pickupPoints = bestPrice !== null && lockPrice !== null
    ? bestPrice - lockPrice
    : null;
  const pickupDollars = pickupPointsToDollars(pickupPoints, pickupLoanAmount(loan));

  return {
    currentInvestor,
    bestInvestor,
    investorRoute,
    bestPrice,
    pickupPoints,
    pickupDollars
  };
}

export interface PickupSelectionExclusionCounts {
  total: number;
  cooldown: number;
  verification: number;
  access: number;
  eligibility: number;
}

export function pickupLoanBestGain(loan: Pick<
  PickupLoan,
  | 'locked_price'
  | 'current_investor_price'
  | 'best_price'
  | 'best_investor'
  | 'ob_investor_name'
  | 'investor_name'
  | 'base_loan_amount'
  | 'trans_details_loan_amt'
>): number | null {
  return pickupLoanBestPricing(loan).pickupPoints;
}

export function pickupLoanInvestor(loan: Pick<PickupLoan, 'ob_investor_name' | 'investor_name'>): string {
  return formatPickupInvestorDisplayName(loan.ob_investor_name || loan.investor_name)
    || 'Unknown investor';
}

export function pickupLoanMatchesGainFilter(loan: PickupLoan, filter: PickupGainFilter): boolean {
  if (filter === 'all') {
    return true;
  }
  const gain = pickupLoanBestGain(loan);
  if (filter === 'trigger') {
    return gain !== null && gain >= 0.5;
  }
  if (filter === 'positive') {
    return gain !== null && gain > 0 && gain < 0.5;
  }
  return gain === null || gain <= 0;
}

export function pickupLoanMatchesVerificationFilter(
  loan: PickupLoan,
  filter: PickupVerificationFilter
): boolean {
  if (filter === 'all') {
    return true;
  }
  return pickupVerificationSeverity({
    status: loan.pickup_verification_status,
    mismatchFields: loan.pickup_verification_mismatch_fields,
    lastVerifiedAt: loan.pickup_last_verified_at,
    canonicalStateChanged: loan.pickup_canonical_state_changed
  }) === filter;
}

export function applyPickupAnalysisFilters(
  loans: PickupLoan[],
  filters: PickupAnalysisFilters
): PickupLoan[] {
  return loans.filter((loan) => (
    pickupLoanMatchesGainFilter(loan, filters.gain)
    && pickupLoanMatchesVerificationFilter(loan, filters.verification)
    && (filters.investor === 'all' || pickupLoanInvestor(loan) === filters.investor)
  ));
}

export function buildPickupAnalysisFilterCounts(
  loans: PickupLoan[],
  filters: PickupAnalysisFilters
): PickupAnalysisFilterCounts {
  const countWith = (overrides: Partial<PickupAnalysisFilters>) => applyPickupAnalysisFilters(
    loans,
    { ...filters, ...overrides }
  ).length;
  const investorNames = Array.from(new Set(loans.map(pickupLoanInvestor)))
    .sort((left, right) => left.localeCompare(right));

  return {
    gain: {
      trigger: countWith({ gain: 'trigger' }),
      positive: countWith({ gain: 'positive' }),
      none: countWith({ gain: 'none' })
    },
    verification: {
      none: countWith({ verification: 'none' }),
      minor: countWith({ verification: 'minor' }),
      major: countWith({ verification: 'major' }),
      not_checked: countWith({ verification: 'not_checked' })
    },
    investors: investorNames.map((investor) => ({
      investor,
      count: countWith({ investor })
    }))
  };
}

export function buildPickupAnalysisGridSummary(loans: PickupLoan[]): PickupAnalysisGridSummary {
  let withPickupCount = 0;
  let potentialDollars = 0;
  let volumeDollars = 0;

  for (const loan of loans) {
    const pricing = pickupLoanBestPricing(loan);
    volumeDollars += pickupLoanAmount(loan) || 0;
    if (pricing.pickupPoints !== null && pricing.pickupPoints >= 0.5) {
      withPickupCount += 1;
      potentialDollars += pricing.pickupDollars || 0;
    }
  }

  return {
    rowCount: loans.length,
    withPickupCount,
    potentialDollars,
    volumeDollars
  };
}

export function pickupSelectionExclusionReason(
  loan: PickupLoan,
  _nowMs = Date.now()
): keyof Omit<PickupSelectionExclusionCounts, 'total'> | null {
  if (isPickupLoanSelectable(loan)) {
    return null;
  }
  if (loan.ob_access_parked_at || loan.pickup_verification_status === 'ob_access_error') {
    return 'access';
  }
  if (
    !loan.pickup_canonical_state_changed
    && (loan.pickup_verification_status === 'waiting_for_ob_sync'
      || loan.pickup_verification_status === 'ob_mismatch')
  ) {
    return 'verification';
  }
  return 'eligibility';
}

export function countPickupSelectionExclusions(
  loans: PickupLoan[],
  nowMs = Date.now()
): PickupSelectionExclusionCounts {
  const counts: PickupSelectionExclusionCounts = {
    total: 0,
    cooldown: 0,
    verification: 0,
    access: 0,
    eligibility: 0
  };
  for (const loan of loans) {
    const reason = pickupSelectionExclusionReason(loan, nowMs);
    if (!reason) {
      continue;
    }
    counts.total++;
    counts[reason]++;
  }
  return counts;
}
